import { z } from 'zod';
// =========================================================
// Roles + statuses
// =========================================================
export const RoleSchema = z.enum([
    'super-admin',
    'tenant-admin',
    'knowledge-manager',
    'tenant-user',
]);
export const TenantRoleSchema = z.enum(['tenant-admin', 'knowledge-manager', 'tenant-user']);
export const TenantStatusSchema = z.enum(['active', 'suspended', 'deleted']);
export const InviteStatusSchema = z.enum(['pending', 'accepted', 'revoked', 'expired']);
export const UserStatusSchema = z.enum(['invited', 'active', 'disabled']);
// =========================================================
// Identifiers + primitives
// =========================================================
/**
 * Tenant slug rules — see docs/data-model.md §4.1.1.
 * Slug becomes a Phase-3 subdomain (`<slug>.agent.cloneai.ca`),
 * so it must satisfy RFC 1035 label rules.
 */
export const SLUG_REGEX = /^[a-z0-9]([a-z0-9-]{0,38}[a-z0-9])?$/;
export const RESERVED_SLUGS = new Set([
    'www',
    'api',
    'dashboard',
    'admin',
    'agent',
    'embed',
    'assets',
    'mail',
    'app',
    'auth',
    'login',
    'console',
    'cdn',
    'static',
    'public',
    'docs',
    'support',
    'status',
    'help',
    'cloneai',
    'ssl',
    'cname',
    'test',
    'staging',
    'prod',
    'dev',
]);
export const SlugSchema = z
    .string()
    .min(2)
    .max(40)
    .regex(SLUG_REGEX, {
    message: 'Slug must be a valid DNS label: lowercase alphanumeric + hyphens, no leading/trailing hyphen.',
})
    .refine((s) => !RESERVED_SLUGS.has(s), { message: 'This slug is reserved.' });
/**
 * Agent slug — same DNS-label rules as tenant slug.
 * Aliased to `SlugSchema` because the rules are identical.
 * Exported under its own name so call-sites read clearly.
 */
export const AgentSlugSchema = SlugSchema;
/** Returns true if the given slug appears in the reserved-slug block-list. */
export function isReservedSlug(slug) {
    return RESERVED_SLUGS.has(slug);
}
export const EmailSchema = z.string().email();
/** Cognito `sub` — UUID-shaped. */
export const CognitoSubSchema = z.string().uuid();
/** ISO-8601 UTC timestamp string. */
export const TimestampSchema = z.string().datetime();
/** Opaque pagination cursor. */
export const CursorSchema = z.string();
/** Standard REST page size (`docs/api.md` §2.1): defaulted server-side. */
export const PaginationLimitSchema = z.coerce.number().int().min(1).max(100);
// =========================================================
// Errors (RFC 7807)
// =========================================================
export const ErrorCodeSchema = z.enum([
    'UNAUTHENTICATED',
    'FORBIDDEN',
    'WRONG_AUDIENCE',
    'TENANT_SUSPENDED',
    'NOT_FOUND',
    'VALIDATION_FAILED',
    'CONFLICT',
    'GONE',
    'UNPROCESSABLE',
    'RATE_LIMITED',
    'INTERNAL',
    'SERVICE_UNAVAILABLE',
]);
export const ProblemSchema = z.object({
    type: z.string().url(),
    title: z.string(),
    status: z.number().int().min(100).max(599),
    detail: z.string().optional(),
    instance: z.string().optional(),
    errorCode: ErrorCodeSchema,
});
export function paginatedSchema(itemSchema) {
    return z.object({
        items: z.array(itemSchema),
        nextCursor: z.string().nullable(),
    });
}
//# sourceMappingURL=common.js.map